import { esc } from '../ui/modal';
import { t } from '../ui/lang';
function formatBytes(n) {
    if (n < 1024)
        return `${n} B`;
    if (n < 1024 * 1024)
        return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
/** "Clicker Runner" clicker-settings tab — upload & deploy code to the host runner. */
export async function renderRunnerCodeTab(panel) {
    panel.innerHTML = `
        <details class="settings-section cc-howto">
            <summary class="settings-section__title cc-howto__summary">${t('clickertab.howto')}</summary>
            <ol class="cc-howto__steps">
                <li>${t('clickertab.runner_howto_1')}</li>
                <li>${t('clickertab.runner_howto_2')}</li>
                <li>${t('clickertab.runner_howto_3')}</li>
                <li>${t('clickertab.runner_howto_4')}</li>
                <li>${t('clickertab.runner_howto_5')}</li>
            </ol>
            <p class="settings-hint">
                ${t('clickertab.runner_howto_running_note')}
            </p>
        </details>
        <div class="settings-section">
            <h3 class="settings-section__title">${t('clickertab.upload_version')}</h3>
            <p class="settings-hint">
                ${t('clickertab.runner_upload_hint')}
            </p>
            <form class="settings-code-form" novalidate>
                <div class="modal__field">
                    <label>${t('clickertab.version')}</label>
                    <input class="settings-input rc-version" type="text" autocomplete="off" placeholder="${t('clickertab.version_placeholder')}" required />
                </div>
                <div class="modal__field">
                    <label>${t('clickertab.note')} <span class="settings-hint" style="display:inline">${t('clickertab.optional')}</span></label>
                    <input class="settings-input rc-note" type="text" autocomplete="off" placeholder="${t('clickertab.note_placeholder')}" />
                </div>
                <div class="modal__field">
                    <label>${t('clickertab.bundle')}</label>
                    <input class="settings-input rc-file" type="file" accept=".gz,.tgz,application/gzip" required />
                </div>
                <button type="submit" class="modal__submit" style="margin-top:12px">${t('clickertab.upload')}</button>
            </form>
            <div class="settings-error modal__error rc-add-error" style="display:none"></div>
        </div>
        <div class="settings-section">
            <h3 class="settings-section__title">${t('clickertab.versions')}</h3>
            <div class="rc-list">${t('common.loading')}</div>
        </div>
        <div class="settings-section">
            <h3 class="settings-section__title">${t('clickertab.runner')}</h3>
            <div class="rc-status">${t('common.loading')}</div>
        </div>
    `;
    const statusEl = panel.querySelector('.rc-status');
    const listEl = panel.querySelector('.rc-list');
    const form = panel.querySelector('.settings-code-form');
    const versionInput = panel.querySelector('.rc-version');
    const noteInput = panel.querySelector('.rc-note');
    const fileInput = panel.querySelector('.rc-file');
    const addError = panel.querySelector('.rc-add-error');
    const showAddErr = (msg) => { addError.textContent = msg; addError.style.display = 'block'; };
    form.addEventListener('submit', async (e) => {
        e.preventDefault();
        addError.style.display = 'none';
        const version = versionInput.value.trim();
        const note = noteInput.value.trim();
        const file = fileInput.files?.[0];
        if (!/^[A-Za-z0-9._-]{1,64}$/.test(version)) {
            showAddErr(t('clickertab.err_version_format'));
            return;
        }
        if (!file) {
            showAddErr(t('clickertab.err_choose_bundle'));
            return;
        }
        const btn = form.querySelector('.modal__submit');
        btn.disabled = true;
        try {
            const buf = await file.arrayBuffer();
            const r = await fetch(`/api/clicker/runner-code?version=${encodeURIComponent(version)}&note=${encodeURIComponent(note)}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/gzip' },
                body: buf,
            });
            if (!r.ok) {
                showAddErr((await r.json()).error ?? t('clickertab.upload_failed'));
                return;
            }
            form.reset();
            void load();
        }
        catch {
            showAddErr(t('clickertab.network_error'));
        }
        finally {
            btn.disabled = false;
        }
    });
    async function load() {
        let data;
        try {
            const r = await fetch('/api/clicker/runner-code');
            if (!r.ok) {
                statusEl.innerHTML = `<p class="settings-hint">${t('clickertab.failed_to_load_dot')}</p>`;
                listEl.innerHTML = '';
                return;
            }
            data = await r.json();
        }
        catch {
            statusEl.innerHTML = `<p class="settings-hint">${t('clickertab.network_error_dot')}</p>`;
            listEl.innerHTML = '';
            return;
        }
        renderStatus(data);
        renderVersions(data);
    }
    function renderStatus(data) {
        if (!data.configured) {
            statusEl.innerHTML = `<p class="settings-hint">${t('clickertab.runner_not_configured')}</p>`;
            return;
        }
        const running = data.online ? (data.version || 'baked') : '—';
        const dot = data.online ? 'status-dot--green' : 'status-dot--red';
        const current = data.versions.find((v) => v.is_current)?.version ?? '';
        let statusBadge;
        if (!data.online)
            statusBadge = `<span class="settings-hint">${t('clickertab.badge_offline')}</span>`;
        else if (current && running === current)
            statusBadge = `<span class="cc-badge cc-badge--ok">${t('clickertab.badge_up_to_date')}</span>`;
        else if (current)
            statusBadge = `<span class="cc-badge cc-badge--drift">${t('clickertab.badge_differs')}</span>`;
        else
            statusBadge = `<span class="settings-hint">${t('clickertab.badge_no_current')}</span>`;
        statusEl.innerHTML = `
            <table class="access-matrix">
                <thead><tr><th>${t('clickertab.runner')}</th><th>${t('clickertab.running')}</th><th>${t('common.status')}</th><th></th></tr></thead>
                <tbody>
                    <tr>
                        <td><span class="status-dot ${dot}"></span> clicker-runner</td>
                        <td>${esc(running)}</td>
                        <td>${statusBadge}</td>
                        <td class="cc-actions">
                            ${data.online && running !== 'baked' ? `<button type="button" class="modal__submit modal__submit--danger rc-reset">${t('clickertab.reset_to_baked')}</button>` : ''}
                        </td>
                    </tr>
                </tbody>
            </table>
            <div class="settings-error modal__error rc-result" style="display:none"></div>
        `;
        wireReset();
    }
    function renderVersions(data) {
        if (data.versions.length === 0) {
            listEl.innerHTML = `<p class="settings-hint">${t('clickertab.no_versions')}</p>`;
            return;
        }
        const running = data.online ? (data.version || 'baked') : '';
        const rows = data.versions.map((v) => `
            <tr data-version="${esc(v.version)}">
                <td>${esc(v.version)}${v.is_current ? ` <span class="cc-badge cc-badge--current">${t('clickertab.badge_current')}</span>` : ''}${running && v.version === running ? ` <span class="cc-badge cc-badge--ok">${t('clickertab.badge_running')}</span>` : ''}</td>
                <td>${esc(v.note)}</td>
                <td title="${esc(v.sha256)}">${formatBytes(v.size)}</td>
                <td>${esc(new Date(v.created_at).toLocaleString())}</td>
                <td class="cc-actions">
                    <button type="button" class="modal__submit rc-deploy" data-version="${esc(v.version)}"${data.configured ? '' : ' disabled'}>${t('clickertab.deploy_to_runner')}</button>
                    ${v.is_current ? '' : `<button type="button" class="modal__submit rc-current" data-version="${esc(v.version)}">${t('clickertab.set_current')}</button>`}
                    <button type="button" class="modal__submit modal__submit--danger rc-del" data-version="${esc(v.version)}">${t('common.delete')}</button>
                </td>
            </tr>
        `).join('');
        listEl.innerHTML = `
            <table class="access-matrix">
                <thead><tr><th>${t('clickertab.version')}</th><th>${t('clickertab.note')}</th><th>${t('clickertab.size')}</th><th>${t('clickertab.uploaded')}</th><th></th></tr></thead>
                <tbody>${rows}</tbody>
            </table>
            <div class="settings-error modal__error rc-deploy-result" style="display:none"></div>
        `;
        wireVersionActions();
    }
    function wireReset() {
        const resultEl = statusEl.querySelector('.rc-result');
        const btn = statusEl.querySelector('.rc-reset');
        if (!btn || !resultEl)
            return;
        let armed = false;
        const orig = btn.textContent ?? t('clickertab.reset_to_baked');
        btn.addEventListener('click', async () => {
            if (!armed) {
                armed = true;
                btn.textContent = t('clickertab.confirm_reset');
                setTimeout(() => { armed = false; btn.textContent = orig; }, 4000);
                return;
            }
            resultEl.style.display = 'none';
            btn.disabled = true;
            btn.textContent = t('clickertab.resetting');
            try {
                const r = await fetch('/api/clicker/runner-code/reset', { method: 'POST' });
                const body = await r.json();
                resultEl.textContent = r.ok
                    ? t('clickertab.runner_reset_ok', { version: body.version ?? 'baked' })
                    : (body.error ?? t('clickertab.reset_failed'));
                resultEl.style.display = 'block';
                void load();
            }
            catch {
                resultEl.textContent = t('clickertab.network_error');
                resultEl.style.display = 'block';
            }
            finally {
                btn.disabled = false;
                armed = false;
                btn.textContent = orig;
            }
        });
    }
    function wireVersionActions() {
        const resultEl = listEl.querySelector('.rc-deploy-result');
        listEl.querySelectorAll('.rc-deploy').forEach((btn) => {
            btn.addEventListener('click', async () => {
                resultEl.style.display = 'none';
                btn.disabled = true;
                const orig = btn.textContent;
                btn.textContent = t('clickertab.deploying');
                try {
                    const version = btn.dataset['version'];
                    const r = await fetch(`/api/clicker/runner-code/${encodeURIComponent(version)}/deploy`, { method: 'POST' });
                    const body = await r.json();
                    resultEl.textContent = r.ok
                        ? t('clickertab.runner_deployed_ok', { version: body.version ?? version })
                        : (body.error ?? t('clickertab.deploy_failed'));
                    resultEl.style.display = 'block';
                    void load();
                }
                catch {
                    resultEl.textContent = t('clickertab.network_error');
                    resultEl.style.display = 'block';
                }
                finally {
                    btn.disabled = false;
                    btn.textContent = orig;
                }
            });
        });
        listEl.querySelectorAll('.rc-current').forEach((btn) => {
            btn.addEventListener('click', async () => {
                btn.disabled = true;
                try {
                    const version = btn.dataset['version'];
                    const r = await fetch(`/api/clicker/runner-code/${encodeURIComponent(version)}/current`, { method: 'POST' });
                    if (r.ok)
                        void load();
                    else
                        btn.disabled = false;
                }
                catch {
                    btn.disabled = false;
                }
            });
        });
        listEl.querySelectorAll('.rc-del').forEach((btn) => {
            let armed = false;
            btn.addEventListener('click', async () => {
                if (!armed) {
                    armed = true;
                    btn.textContent = t('common.confirm');
                    setTimeout(() => { armed = false; btn.textContent = t('common.delete'); }, 4000);
                    return;
                }
                btn.disabled = true;
                try {
                    const version = btn.dataset['version'];
                    const r = await fetch(`/api/clicker/runner-code/${encodeURIComponent(version)}`, { method: 'DELETE' });
                    if (r.ok)
                        void load();
                    else {
                        btn.disabled = false;
                        armed = false;
                        btn.textContent = t('common.delete');
                    }
                }
                catch {
                    btn.disabled = false;
                }
            });
        });
    }
    void load();
}
